import type {
  ReleaseAppRow,
  ReleaseAppVersionRow,
  ReleaseArtifactRow,
  ReleaseChannelRow,
  ReleasePromotionRow,
  RuntimeConfigRow,
} from '../api';
import type { PageId } from '../types/navigation';

export type ProjectReadinessStatus = 'healthy' | 'warning' | 'blocked';

export type ProjectReadinessCheck = {
  id: string;
  label: string;
  detail: string;
  status: ProjectReadinessStatus;
  page: PageId;
  action: string;
};

export type ProjectReadiness = {
  checks: ProjectReadinessCheck[];
  blocked: number;
  warnings: number;
  healthy: number;
  servingTargets: number;
  totalTargets: number;
  recoverableTargets: number;
  legacyTargets: number;
};

type ProjectReadinessInput = {
  releaseApps: ReleaseAppRow[];
  releaseAppVersions: ReleaseAppVersionRow[];
  artifacts: ReleaseArtifactRow[];
  channels: ReleaseChannelRow[];
  promotions: ReleasePromotionRow[];
  runtimeConfigs: RuntimeConfigRow[];
  now?: Date;
};

function plural(count: number, singular: string, pluralForm = `${singular}s`) {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

function ageInDays(value: string | undefined, now: Date) {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return null;
  return Math.max(0, Math.round((now.getTime() - timestamp) / 86_400_000));
}

export function assessProjectReadiness({
  releaseApps,
  releaseAppVersions,
  artifacts,
  channels,
  promotions,
  runtimeConfigs,
  now = new Date(),
}: ProjectReadinessInput): ProjectReadiness {
  const activeApps = releaseApps.filter((app) => app.isActive);
  const activeVersions = releaseAppVersions.filter((version) => version.isActive);
  const versionedAppIds = new Set(activeVersions.map((version) => version.releaseAppId));
  const appsMissingVersions = activeApps.filter(
    (app) => !versionedAppIds.has(app.releaseAppId)
  );

  const runtimeVersionIds = new Set(
    runtimeConfigs
      .filter((config) => config.isActive && config.releaseAppVersionId)
      .map((config) => config.releaseAppVersionId as string)
  );
  const versionsMissingRuntime = activeVersions.filter(
    (version) => !runtimeVersionIds.has(version.releaseAppVersionId)
  );

  const activeChannels = channels.filter((channel) => channel.isActive);
  const servingChannels = activeChannels.filter((channel) => channel.releaseArtifactId);
  const unservedChannels = activeChannels.filter((channel) => !channel.releaseArtifactId);
  const legacyChannels = channels.filter(
    (channel) => !channel.releaseAppId || !channel.releaseAppVersionId
  );
  const promotionCountByChannel = new Map<string, number>();
  promotions.forEach((promotion) => {
    if (!promotion.releaseChannelId) return;
    promotionCountByChannel.set(
      promotion.releaseChannelId,
      (promotionCountByChannel.get(promotion.releaseChannelId) || 0) + 1
    );
  });
  const recoverableChannels = servingChannels.filter(
    (channel) => (promotionCountByChannel.get(channel.releaseChannelId) || 0) > 1
  );

  const latestDeployment = [...promotions]
    .filter((promotion) => promotion.created)
    .sort((a, b) => (b.created || '').localeCompare(a.created || ''))[0];
  const deploymentAge = ageInDays(latestDeployment?.created, now);

  const checks: ProjectReadinessCheck[] = [
    {
      id: 'platform-versions',
      label: 'Platform versions',
      detail:
        activeApps.length === 0
          ? 'No iOS or Android platform tracks are registered.'
          : appsMissingVersions.length > 0
            ? `${appsMissingVersions.map((app) => app.name).join(', ')} ${appsMissingVersions.length === 1 ? 'has' : 'have'} no store version.`
            : `${plural(activeApps.length, 'platform track')} covered by ${plural(activeVersions.length, 'store version')}.`,
      status:
        activeApps.length === 0 || appsMissingVersions.length > 0
          ? 'blocked'
          : 'healthy',
      page: 'app-releases',
      action: activeApps.length === 0 ? 'Add platform' : 'Review versions',
    },
    {
      id: 'runtime-coverage',
      label: 'Runtime coverage',
      detail:
        activeVersions.length === 0
          ? 'Register a store version before adding runtime rules.'
          : versionsMissingRuntime.length > 0
            ? `${plural(versionsMissingRuntime.length, 'store version')} cannot resolve an active runtime rule.`
            : `All ${plural(activeVersions.length, 'store version')} resolve an active runtime rule.`,
      status:
        activeVersions.length === 0 || versionsMissingRuntime.length > 0
          ? 'blocked'
          : 'healthy',
      page: 'runtime-config',
      action: 'Open runtime rules',
    },
    {
      id: 'bundle-inventory',
      label: 'Bundle inventory',
      detail:
        artifacts.length > 0
          ? `${plural(artifacts.length, 'bundle')} available to deploy.`
          : 'No CI bundle has been uploaded for this project.',
      status: artifacts.length > 0 ? 'healthy' : 'blocked',
      page: 'releases',
      action: 'View bundles',
    },
    {
      id: 'delivery-coverage',
      label: 'Delivery coverage',
      detail:
        channels.length === 0
          ? 'No exact app-version targets are configured.'
          : legacyChannels.length > 0
            ? `${plural(legacyChannels.length, 'legacy target')} cannot be traced to an exact app version.`
            : unservedChannels.length > 0
              ? `${servingChannels.length} of ${activeChannels.length} active targets are serving; ${unservedChannels.length} await a bundle.`
              : `All ${plural(activeChannels.length, 'active target')} are serving a bundle.`,
      status:
        channels.length === 0 || legacyChannels.length > 0
          ? 'blocked'
          : unservedChannels.length > 0
            ? 'warning'
            : 'healthy',
      page: 'channels',
      action: legacyChannels.length > 0 ? 'Inspect targets' : 'View live state',
    },
    {
      id: 'recovery-coverage',
      label: 'Recovery coverage',
      detail:
        servingChannels.length === 0
          ? 'No serving target exists to recover.'
          : recoverableChannels.length < servingChannels.length
            ? `${recoverableChannels.length} of ${servingChannels.length} serving targets have a previous exact deployment.`
            : `All ${plural(servingChannels.length, 'serving target')} have a restore point.`,
      status:
        servingChannels.length > 0 && recoverableChannels.length === servingChannels.length
          ? 'healthy'
          : 'warning',
      page: 'rollback',
      action: 'Review recovery',
    },
    {
      id: 'release-activity',
      label: 'Release activity',
      detail:
        deploymentAge === null
          ? 'No deployment history is recorded.'
          : deploymentAge > 30
            ? `The last target change was ${deploymentAge} days ago.`
            : `A target changed ${deploymentAge === 0 ? 'today' : `${deploymentAge} days ago`}.`,
      status:
        deploymentAge === null || deploymentAge > 30 ? 'warning' : 'healthy',
      page: 'promotions',
      action: 'View history',
    },
  ];

  return {
    checks,
    blocked: checks.filter((check) => check.status === 'blocked').length,
    warnings: checks.filter((check) => check.status === 'warning').length,
    healthy: checks.filter((check) => check.status === 'healthy').length,
    servingTargets: servingChannels.length,
    totalTargets: channels.length,
    recoverableTargets: recoverableChannels.length,
    legacyTargets: legacyChannels.length,
  };
}
